import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
  Currency,
  FiatCurrency,
} from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { CryptoIcon } from '@sorare/core/src/atoms/icons/CryptoIcon';
import { iconSort } from '@sorare/core/src/atoms/icons/system/iconSort.ts';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BodyM, LabelM } from '@sorare/core/src/atoms/typography';
import { FRONTEND_ASSET_HOST } from '@sorare/core/src/constants/assets';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useAmountWithConversion } from '@sorare/core/src/hooks/useAmountWithConversion';
import { useCurrentUserCurrencies } from '@sorare/core/src/hooks/useCurrentUserCurrencies';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { cryptocurrencySymbols } from '@sorare/core/src/lib/cryptocurrency';

const CurrencyContainer = styled(SBHorizontal)`
  gap: var(--double-unit);
  padding: var(--double-unit);
  background-color: var(--c-nd-50);
  border-radius: var(--double-unit);
`;

const TradeIconContainer = styled(Horizontal).attrs({ gap: 0, center: true })`
  width: var(--quadruple-unit);
  height: var(--quadruple-unit);
  border-radius: 50%;
  background: var(--c-white);
  align-self: center;
  margin: calc(-1 * var(--intermediate-unit)) 0;
`;

const CurrenciesContainer = styled(Vertical)`
  width: 100%;
`;

const Flag = styled.img`
  width: var(--triple-unit);
  height: var(--double-unit);
  object-fit: contain;
`;

const flagByFiatCurrency = (fiatCurrency: FiatCurrency) => {
  switch (fiatCurrency) {
    case FiatCurrency.USD:
      return `${FRONTEND_ASSET_HOST}/flags/us.svg`;
    case FiatCurrency.GBP:
      return `${FRONTEND_ASSET_HOST}/flags/gb.svg`;
    case FiatCurrency.EUR:
      return `${FRONTEND_ASSET_HOST}/flags/eu.svg`;
    default:
      return '';
  }
};

export const MonetaryRewardConversion = ({
  displayAmount,
  displayCurrency,
}: {
  displayAmount: number;
  displayCurrency: FiatCurrency;
}) => {
  const { currentUser, cryptocurrency } = useCurrentUserContext();
  const { toMonetaryAmountInReferenceCurrency } = useMonetaryAmount();
  const { fiatCurrency } = useCurrentUserCurrencies();
  const { formatNumber } = useIntlContext();
  const formattedDisplayAmount = formatNumber(displayAmount, {
    style: 'currency',
    currency: displayCurrency,
    currencyDisplay: 'narrowSymbol',
    maximumFractionDigits: 0,
  });

  const rewardCurrency =
    currentUser?.userSettings.rewardCurrency || Currency.ETH;

  const { main } = useAmountWithConversion({
    monetaryAmount: toMonetaryAmountInReferenceCurrency(
      displayAmount * 100,
      displayCurrency
    ),
    primaryCurrency: rewardCurrency,
  });

  if (rewardCurrency === Currency.FIAT && fiatCurrency === displayCurrency) {
    return null;
  }

  return (
    <CurrenciesContainer gap={2}>
      <BodyM color="var(--c-nd-600)">
        <FormattedMessage defaultMessage="Converting to local currency:" />
      </BodyM>
      <CurrenciesContainer gap={0}>
        <CurrencyContainer>
          <Horizontal>
            <Flag
              src={flagByFiatCurrency(displayCurrency)}
              alt={displayCurrency}
            />
            <LabelM color="var(--c-nd-600)">{displayCurrency}</LabelM>
          </Horizontal>
          <LabelM color="var(--c-nd-600)">{formattedDisplayAmount}</LabelM>
        </CurrencyContainer>
        <TradeIconContainer>
          <FontAwesomeIcon color="var(--c-black)" icon={iconSort} />
        </TradeIconContainer>
        <CurrencyContainer>
          <Horizontal>
            {rewardCurrency === Currency.ETH ? (
              <CryptoIcon cryptocurrency={cryptocurrency} />
            ) : (
              <Flag
                src={flagByFiatCurrency(fiatCurrency)}
                alt={rewardCurrency}
              />
            )}
            <LabelM bold>
              {rewardCurrency === Currency.ETH
                ? cryptocurrencySymbols[cryptocurrency]
                : fiatCurrency}
            </LabelM>
          </Horizontal>
          <LabelM bold>≈ {main}</LabelM>
        </CurrencyContainer>
      </CurrenciesContainer>
    </CurrenciesContainer>
  );
};
